import { Badge, Box, Flex, HStack, Heading, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheckSquare } from "react-icons/fi";
import type { BuyerPublicBidDetailDto, PublicBidQualificationResponseDto } from "../../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../../common/documents/DocumentsAsOwnerTable";
import { SELECTION_CRITERION_TYPE_OPTIONS } from "../../../sourcing-events/buyer/public/utils/publicProcurementOptions";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import { parseCriterionKey } from "../../../../common/qualification/criterionKey";
import { FulfilmentTag } from "./FulfilmentTag";
import { getBidQualificationProgress, getBidQualificationRows } from "./bidQualification";

interface Props {
  detail: BuyerPublicBidDetailDto;
}

export const QualificationsTab = ({ detail }: Props) => {
  const { t } = useTranslation();
  const responses = getBidQualificationRows(detail);
  const { fulfilled, total, hasRequirements } = getBidQualificationProgress(detail);

  if (!hasRequirements) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        py="16"
        rounded="xl"
        bg="smBackground"
        border="1px dashed"
        borderColor="smBorder"
        color="smMuted"
      >
        <Text fontWeight="medium">{t("No qualification requirements")}</Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="6">
      <SectionCard icon={FiCheckSquare} title={t("Qualification requirements")}>
        <Text color="smMuted" fontSize="sm">
          {t("{{fulfilled}} of {{total}} fulfilled", { fulfilled, total })}
        </Text>
      </SectionCard>

      {responses.map((response) => (
        <QualificationCard key={response.criterionKey} response={response} />
      ))}
    </Stack>
  );
};

const QualificationCard = ({ response }: { response: PublicBidQualificationResponseDto }) => {
  const { t } = useTranslation();
  const { type, name } = parseCriterionKey(response.criterionKey);
  const typeOption = type ? SELECTION_CRITERION_TYPE_OPTIONS.find((o) => o.value === type) : undefined;
  const evidence = response.evidenceDocuments ?? [];

  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px={{ base: "4", md: "6" }} py="5">
      <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="3" flexWrap="wrap" mb="3">
        <HStack spacing="3" align="center" flexWrap="wrap">
          {typeOption && (
            <Badge
              bg={typeOption.badgeBg}
              color={typeOption.badgeColor}
              textTransform="uppercase"
              px="2"
              py="0.5"
              borderRadius="md"
            >
              {t(typeOption.value)}
            </Badge>
          )}
          <Heading as="h3" size="sm" color="smText">
            {name}
          </Heading>
        </HStack>
        <FulfilmentTag fulfilled={response.fulfilled} />
      </Flex>

      {response.description && (
        <Text fontSize="sm" color="smText" whiteSpace="pre-wrap" mb={evidence.length > 0 ? "4" : "0"}>
          {response.description}
        </Text>
      )}

      {evidence.length > 0 && (
        <Box mt="3">
          <Text fontSize="xs" fontWeight="semibold" color="smMuted" textTransform="uppercase" letterSpacing="wide" mb="2">
            {t("Evidence")}
          </Text>
          <DocumentsAsOwnerTable documents={evidence} />
        </Box>
      )}
    </Box>
  );
};
